import React, { useState, useEffect, useMemo } from 'react';
import { 
  CurrencyDollarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { apiClient } from '../utils/api';
import { formatCurrency, formatPercentage, cn } from '../utils/helpers';
import { ProfitData } from '../types';
import io, { Socket } from 'socket.io-client';
import { getSocketUrl } from '../utils/api';

interface PeriodProfit {
  label: string;
  value: number;
  percentage: number;
  period: 'today' | 'week' | 'month';
}

const ProfitStatus: React.FC = () => {
  const { theme } = useTheme();
  const [profitData, setProfitData] = useState<ProfitData>({
    totalProfit: 0,
    todayProfit: 0,
    weekProfit: 0,
    monthProfit: 0,
    winRate: 0,
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Fetch profit data
  useEffect(() => {
    const fetchProfitData = async () => {
      try {
        const response = await apiClient.get<ProfitData>('/api/profit/status');
        if (response.success && response.data) {
          setProfitData(response.data);
        }
      } catch (error) {
        console.error('Error fetching profit data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfitData();
  }, []);

  // WebSocket connection
  useEffect(() => {
    const socketInstance = io(getSocketUrl(), {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
    });

    socketInstance.on('profitUpdate', (data: ProfitData) => {
      setProfitData(data);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // Calculate percentages
  const profitPercentages = useMemo(() => {
    const base = 10000; // Assume starting balance
    return {
      total: profitData.totalProfit ? (profitData.totalProfit / base) * 100 : 0,
      today: profitData.todayProfit ? (profitData.todayProfit / base) * 100 : 0,
      week: profitData.weekProfit ? (profitData.weekProfit / base) * 100 : 0,
      month: profitData.monthProfit ? (profitData.monthProfit / base) * 100 : 0,
    };
  }, [profitData]);

  // Period profits for rendering
  const periodProfits: PeriodProfit[] = useMemo(() => [
    {
      label: '오늘',
      value: profitData.todayProfit,
      percentage: profitPercentages.today,
      period: 'today'
    },
    {
      label: '이번 주',
      value: profitData.weekProfit,
      percentage: profitPercentages.week,
      period: 'week'
    },
    {
      label: '이번 달',
      value: profitData.monthProfit,
      percentage: profitPercentages.month,
      period: 'month'
    }
  ], [profitData, profitPercentages]);

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(
        'p-4 h-full',
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      )}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-1/2" />
          <div className="h-20 bg-gray-300 dark:bg-gray-700 rounded" />
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-300 dark:bg-gray-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'p-4 h-full',
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <CurrencyDollarIcon className="h-5 w-5 text-green-500" />
          <h3 className="text-lg font-semibold">
            수익 현황
          </h3>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-xs text-green-600 dark:text-green-400 font-medium">
            실시간
          </span>
        </div>
      </div>

      {/* Total Profit */}
      <div className="mb-6">
        <div className={cn(
          'rounded-lg p-4 border',
          profitData.totalProfit >= 0
            ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700'
            : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-red-200 dark:border-red-700'
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className={cn(
                'text-sm font-medium mb-1',
                profitData.totalProfit >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}>
                총 수익
              </p>
              <p className={cn(
                'text-2xl font-bold',
                profitData.totalProfit >= 0
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-red-700 dark:text-red-300'
              )}>
                {formatCurrency(profitData.totalProfit)}
              </p>
            </div>
            <div className="flex items-center space-x-1">
              <ArrowTrendingUpIcon className={cn(
                'h-5 w-5',
                profitData.totalProfit >= 0 ? 'text-green-500' : 'text-red-500'
              )} />
              <span className={cn(
                'text-sm font-semibold',
                profitData.totalProfit >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}>
                {formatPercentage(profitPercentages.total)}
              </span>
            </div>
          </div>
          
          {/* Win Rate */}
          {profitData.totalTrades > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  승률: {formatPercentage(profitData.winRate, false)}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {profitData.winningTrades}승 {profitData.losingTrades}패
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Period Profits */}
      <div className="space-y-3">
        {periodProfits.map((period) => (
          <div
            key={period.period}
            className={cn(
              'flex items-center justify-between p-3 rounded-lg',
              theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
            )}
          >
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {period.label}
              </p>
              <p className="text-lg font-semibold">
                {formatCurrency(period.value)}
              </p>
            </div>
            <div className="flex items-center space-x-1">
              <ArrowTrendingUpIcon className={cn(
                'h-4 w-4',
                period.value >= 0 ? 'text-green-500' : 'text-red-500'
              )} />
              <span className={cn(
                'text-sm font-medium',
                period.value >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}>
                {formatPercentage(period.percentage)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Stats Summary */}
      {profitData.totalTrades > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">거래 횟수</p>
              <p className="text-sm font-semibold">{profitData.totalTrades}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">평균 수익</p>
              <p className="text-sm font-semibold">
                {formatCurrency(profitData.totalProfit / profitData.totalTrades)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">최고 승률</p>
              <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                {formatPercentage(profitData.winRate, false)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ProfitStatus);